import { useEffect, useState } from "react";
import { ArrowUpRight, BatteryCharging, BriefcaseBusiness, CircleDollarSign, Clock3, FileDown, FileSpreadsheet, MapPin, Plus, WalletCards, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, money } from "../api";
import { useAuth } from "../auth";
import AlertsPanel from "../components/AlertsPanel";
import CurrencyBreakdown from "../components/CurrencyBreakdown";
import CompanyScopeBar from "../components/CompanyScopeBar";
import TurkeyMap from "../components/TurkeyMap";

const fmtMW = (n) => (n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 1 });
const STATUS_TONE = { "Teklif": "st-blue", "Devam Ediyor": "st-amber", "Durduruldu": "st-red", "Tamamlandı": "st-green", "İptal Edildi": "st-slate", "Belirtilmemiş": "st-slate" };

function Stat({ label, value, detail, icon: Icon, tone, testId }) {
  return (
    <div className="stat" data-testid={testId}>
      <div className={`stat-icon ${tone}`}><Icon size={18} /></div>
      <div>
        <div className="eyebrow">{label}</div>
        <strong>{value}</strong>
        <span className="stat-detail">{detail}</span>
      </div>
    </div>
  );
}

const greet = () => { const h = new Date().getHours(); return h < 12 ? "Günaydın" : h < 18 ? "İyi günler" : "İyi akşamlar"; };

export default function Dashboard({ setActive, company, setCompany }) {
  const { user, can } = useAuth();
  const [data, setData] = useState({ kpis: {}, projects: [], payments: [], breakdown: {}, alerts: null });
  const [pf, setPf] = useState(null);
  const [profitView, setProfitView] = useState("project");
  const [currency, setCurrency] = useState("TRY");

  const companyParam = company ? `&company=${encodeURIComponent(company)}` : "";
  useEffect(() => { api.get(`/dashboard?currency=${currency}${companyParam}`).then(r => setData(r.data)).catch(() => toast.error("Sunucuya bağlanılamadı")); }, [currency, company]);
  useEffect(() => { api.get(`/portfolio/stats${company ? `?company=${encodeURIComponent(company)}` : ""}`).then(r => setPf(r.data)).catch(() => {}); }, [company]);

  const kpis = data.kpis || {};
  const chart = [42, 48, 44, 55, 51, 64, 61, 69, 63, 75, 71, 82];
  const marginPct = kpis.portfolio_value ? Math.round((1 - (kpis.total_paid || 0) / kpis.portfolio_value) * 100) : 0;
  const pdf = async () => { try { await downloadFile(`/reports/pdf/portfolio?currency=${currency}`, "portfoy-raporu.pdf"); toast.success("PDF indirildi"); } catch { toast.error("PDF oluşturulamadı"); } };

  return (
    <main className="content" data-testid="page-dashboard">
      <div className="page-head">
        <div>
          <span className="section-kicker">GÜNCEL PORTFÖY DURUMU</span>
          <h1>{greet()}, {(user?.name || "").split(" ")[0]}.</h1>
          <p>{kpis.total_projects || 0} proje · {kpis.employers || 0} işveren · {kpis.subcontractors || 0} alt yüklenici. Konsolide görünüm: <b>{currency}</b>{data.rates_missing && <em className="warn-inline" data-testid="rates-missing-warning"> · Bazı tutarlar için kur tanımlı değil</em>}</p>
        </div>
        <div className="head-actions">
          <div className="currency-switch" data-testid="dashboard-currency-switch">
            {["TRY", "USD", "EUR"].map(c => (
              <button key={c} className={currency === c ? "selected" : ""} onClick={() => setCurrency(c)} data-testid={`currency-${c.toLowerCase()}-button`}>{c}</button>
            ))}
          </div>
          <button className="import-btn" onClick={pdf} data-testid="portfolio-pdf-button"><FileDown size={15} /> PDF Rapor</button>
          {can("admin") && <button className="import-btn" onClick={() => setActive("import")} data-testid="excel-import-button"><FileSpreadsheet size={16} /> Excel'den aktar</button>}
        </div>
      </div>

      <CompanyScopeBar company={company} setCompany={setCompany} />

      <div className="stats-grid">
        <Stat testId="kpi-active-projects" label="Aktif Proje" value={kpis.active_projects || 0} detail={`${kpis.total_projects || 0} toplam proje`} icon={BriefcaseBusiness} tone="amber" />
        <Stat testId="kpi-portfolio" label={`Portföy Değeri (${currency})`} value={money(kpis.portfolio_value, currency)} detail="Anlaşılan bedel · konsolide" icon={CircleDollarSign} tone="blue" />
        <Stat testId="kpi-paid" label="Tahsil Edilen" value={money(kpis.total_paid, currency)} detail={`Portföyün %${kpis.portfolio_value ? Math.round((kpis.total_paid / kpis.portfolio_value) * 100) : 0}'i`} icon={WalletCards} tone="green" />
        <Stat testId="kpi-pending" label="Bekleyen Ödeme" value={money(kpis.pending_payments, currency)} detail={`Net akış: ${money(kpis.net_cashflow, currency)}`} icon={Clock3} tone="red" />
      </div>

      {pf && (
        <>
          <div className="stats-grid capacity-grid" data-testid="capacity-kpis">
            <Stat testId="kpi-mwm" label="Toplam Kurulu Güç" value={`${fmtMW(pf.capacity.total_mwm)} MWm`} detail="Mekanik kapasite" icon={Zap} tone="amber" />
            <Stat testId="kpi-mwe" label="Elektrik Gücü" value={`${fmtMW(pf.capacity.total_mwe)} MWe`} detail="Üretim kapasitesi" icon={Zap} tone="blue" />
            <Stat testId="kpi-mwh" label="Depolama Enerjisi" value={`${fmtMW(pf.capacity.total_mwh)} MWh`} detail="Batarya kapasitesi" icon={BatteryCharging} tone="green" />
            <Stat testId="kpi-cap-projects" label="Kapasiteli Proje" value={pf.capacity.projects_with_capacity || 0} detail={`${pf.provinces.length} ilde dağılım`} icon={MapPin} tone="red" />
          </div>

          <div className="grid-map-status">
            <section className="panel map-panel" data-testid="map-panel">
              <div className="panel-head"><div><span className="eyebrow">COĞRAFİ DAĞILIM</span><h2>İl bazında kurulu güç</h2></div><small className="rate-note">İl üzerine gelin</small></div>
              <TurkeyMap provinces={pf.provinces} />
            </section>
            <section className="panel status-panel" data-testid="status-breakdown">
              <div className="panel-head"><div><span className="eyebrow">PROJE DURUMU</span><h2>Süreç dağılımı</h2></div></div>
              <div className="status-list">
                {(() => { const mx = Math.max(1, ...pf.status_breakdown.map(s => s.count)); return pf.status_breakdown.map((s) => (
                  <div className="status-bar-row" key={s.status} data-testid={`status-row-${s.status}`}>
                    <span className="status-name">{s.status}</span>
                    <div className="status-track"><div className={`status-fill ${STATUS_TONE[s.status] || "st-slate"}`} style={{ width: `${Math.round(s.count / mx * 100)}%` }} /></div>
                    <b>{s.count}</b>
                  </div>
                )); })()}
              </div>
              <div className="status-total">Toplam <b>{pf.status_breakdown.reduce((a, s) => a + s.count, 0)}</b> proje</div>
            </section>
          </div>

          <section className="panel profit-detail-panel" data-testid="profit-panel">
            <div className="panel-head">
              <div><span className="eyebrow">BEKLENEN KÂR</span><h2>Gelir − Alt yüklenici maliyeti</h2></div>
              <div className="segmented">
                <button className={profitView === "project" ? "selected" : ""} onClick={() => setProfitView("project")} data-testid="profit-view-project">Proje bazında</button>
                <button className={profitView === "employer" ? "selected" : ""} onClick={() => setProfitView("employer")} data-testid="profit-view-employer">İşveren bazında</button>
              </div>
            </div>
            <div className="profit-totals">
              <span><small>TOPLAM GELİR</small><b>{money(pf.totals.revenue)}</b></span>
              <span><small>ALT YÜKLENİCİ MALİYETİ</small><b>{money(pf.totals.cost)}</b></span>
              <span><small>BEKLENEN KÂR</small><b className="profit-pos">{money(pf.totals.profit)}</b></span>
            </div>
            <div className="profit-list" data-testid="profit-list">
              {(() => {
                const rows = profitView === "project" ? pf.profit_by_project : pf.profit_by_employer;
                const mx = Math.max(1, ...rows.map(r => Math.abs(r.profit)));
                return rows.slice(0, 12).map((r, i) => (
                  <div className={`profit-row ${profitView === "project" ? "clickable" : ""}`} key={i} data-testid={`profit-row-${i}`}
                    onClick={() => profitView === "project" && r.project_id && setActive("projects", r.project_id)}>
                    <div className="profit-label">
                      <b>{profitView === "project" ? r.name : r.employer_short}</b>
                      <small>{profitView === "project" ? r.employer_short : `${r.project_count} proje`}</small>
                    </div>
                    <div className="profit-bar-track"><div className={`profit-bar ${r.profit >= 0 ? "pos" : "neg"}`} style={{ width: `${Math.round(Math.abs(r.profit) / mx * 100)}%` }} /></div>
                    <div className="profit-nums">
                      <small>Gelir {money(r.revenue, r.currency)}</small>
                      <small>Maliyet {money(r.cost, r.currency)}</small>
                      <b className={r.profit >= 0 ? "profit-pos" : "profit-neg"}>{money(r.profit, r.currency)}</b>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </section>
        </>
      )}

      <div className="grid-main">
        <AlertsPanel alerts={data.alerts} compact onOpenProject={(pid) => pid && setActive("projects", pid)} />
        <section className="panel profit-panel">
          <div className="panel-head"><div><span className="eyebrow">KÂRLILIK</span><h2>Genel marj</h2></div></div>
          <div className="donut" style={{ background: `conic-gradient(var(--amber) 0 ${Math.max(0, Math.min(100, 100 - marginPct))}%, var(--blue) ${Math.max(0, Math.min(100, 100 - marginPct))}% 100%)` }}>
            <div className="donut-inner"><strong>%{marginPct}</strong><span>kalan tahsilat oranı</span></div>
          </div>
          <div className="legend">
            <span><i className="dot amber-dot" />Tahsil edilen <b>{money(kpis.total_paid, currency)}</b></span>
            <span><i className="dot blue-dot" />Kalan <b>{money((kpis.portfolio_value || 0) - (kpis.total_paid || 0), currency)}</b></span>
            <span><i className="dot slate-dot" />Bekleyen ödeme <b>{money(kpis.pending_payments, currency)}</b></span>
          </div>
        </section>
      </div>

      <CurrencyBreakdown breakdown={data.breakdown} latestRate={data.latest_rate} />

      <section className="panel cash-panel">
        <div className="panel-head">
          <div><span className="eyebrow">PERFORMANS</span><h2>Nakit akışı</h2></div>
          <button className="view-all" onClick={() => setActive("finance")} data-testid="cashflow-detail-button">Aylık detay <ArrowUpRight size={14} /></button>
        </div>
        <div className="cash-total">
          <strong>{money(kpis.net_cashflow || 0, currency)}</strong>
          <span className={kpis.net_cashflow >= 0 ? "positive" : "negative"}><ArrowUpRight size={14} /> net</span>
          <small>gelen {money(kpis.incoming_total, currency)} · giden {money(kpis.outgoing_total, currency)}</small>
        </div>
        <div className="chart-wrap">
          <div className="bars">
            {chart.map((v, i) => (
              <div className="bar-col" key={i}>
                <div className="bar-value" style={{ height: `${v}%` }}><i /></div>
                <small>{["Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara","Oca","Şub"][i]}</small>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="panel projects-panel">
        <div className="panel-head">
          <div><span className="eyebrow">PROJE PORTFÖYÜ</span><h2>Son projeler</h2></div>
          <button className="view-all" onClick={() => setActive("projects")} data-testid="view-all-projects-button">Tümünü gör <ArrowUpRight size={14} /></button>
        </div>
        <div className="project-table" data-testid="dashboard-projects-table">
          <div className="table-row table-head"><span>PROJE / İŞVEREN</span><span>DURUM</span><span>KAPASİTE</span><span>BÜTÇE / TAHSİL</span><span>KALAN</span><span></span></div>
          {(data.projects || []).map((p, i) => (
            <div className="table-row clickable" key={p.id} data-testid={`project-row-${i}`} onClick={() => setActive("projects", p.id)}>
              <div className="project-name">
                <div className={`project-badge badge-${i % 3}`}>{(p.name || "").split(" ").map(x => x[0]).join("").slice(0, 2)}</div>
                <div><b>{p.name}</b><small>{p.employer_short}{p.investor_short && p.investor_short !== p.employer_short ? ` · Yatırımcı: ${p.investor_short}` : ""}</small></div>
              </div>
              <span><em className={`status ${(p.process_status || p.status) === "Teklif" ? "status-blue" : (p.process_status || p.status) === "Tamamlandı" ? "status-green" : (p.process_status || p.status) === "İptal Edildi" || (p.process_status || p.status) === "Durduruldu" ? "status-red" : "status-amber"}`}>{p.process_status || p.status || "—"}</em></span>
              <span className="mono-small">{p.capacity_production || p.capacity || "—"}</span>
              <div className="amount-cell"><b>{money(p.agreed_amount, p.currency)}</b><small>{money(p.paid_amount, p.currency)} tahsil</small></div>
              <b className="margin">{money((p.agreed_amount || 0) - (p.paid_amount || 0), p.currency)}</b>
              <button className="row-action" data-testid={`project-action-${i}`}>›</button>
            </div>
          ))}
        </div>
      </section>

      <section className="panel payments-panel">
        <div className="panel-head">
          <div><span className="eyebrow">SON HAREKETLER</span><h2>Son ödeme hareketleri</h2></div>
          <button className="view-all" onClick={() => setActive("payments")} data-testid="add-payment-button"><Plus size={14} /> Ödemeler</button>
        </div>
        <div className="payment-list" data-testid="dashboard-payments-list">
          {(data.payments || []).slice(0, 5).map((p, i) => (
            <div className="payment-row" key={p.id || i}>
              <div className="payment-icon"><WalletCards size={16} /></div>
              <div className="payment-info"><b>{p.subcontractor_short || p.project_name}</b><small>{p.project_name} · {p.stage}{p.date ? ` · ${p.date.slice(0, 10)}` : ""}</small></div>
              <strong>{money(p.amount, p.currency)}</strong>
              <em className={`status ${p.status === "Bekliyor" ? "status-amber" : p.status === "Planlanan" ? "status-blue" : "status-green"}`}>{p.status}</em>
              <button className="row-action" onClick={() => p.project_id && setActive("projects", p.project_id)} data-testid={`payment-action-${i}`}>›</button>
            </div>
          ))}
          {!(data.payments || []).length && <div className="empty-hint">Henüz ödeme kaydı yok.</div>}
        </div>
      </section>
    </main>
  );
}
